import { useEffect, useRef } from 'react';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
export const CALENDLY_URL = 'https://calendly.com/mashmari/30min';

let calendlyScriptPromise;

function loadCalendlyScript() {
  if (window.Calendly) {
    return Promise.resolve();
  }
  if (calendlyScriptPromise) return calendlyScriptPromise;

  const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`);
  if (existing) {
    calendlyScriptPromise = new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
    return calendlyScriptPromise;
  }

  calendlyScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Calendly script failed to load'));
    document.head.appendChild(script);
  });
  return calendlyScriptPromise;
}

export default function CalendlyInline({ onEventScheduled }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin.endsWith('calendly.com') && e.data?.event === 'calendly.event_scheduled') {
        onEventScheduled?.();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onEventScheduled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let cancelled = false;

    loadCalendlyScript()
      .then(() => {
        if (cancelled || !container.isConnected) return;
        if (!container.querySelector('iframe') && window.Calendly?.initInlineWidget) {
          window.Calendly.initInlineWidget({ url: container.dataset.url, parentElement: container });
        }
      })
      .catch((error) => { if (!cancelled) console.error(error); });

    return () => { cancelled = true; container.replaceChildren(); };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="calendly-inline-widget"
      className="calendly-inline-widget w-full"
      data-url={CALENDLY_URL}
      style={{ minWidth: 320, height: 'clamp(700px, 80vh, 900px)' }}
    />
  );
}
